/**
 * RelatedLeadsTable - Display related leads for a customer
 */

import React, { useCallback } from "react";
import { Table, Tag, Button, Typography } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

const { Text, Link } = Typography;

const RelatedLeadsTable = ({ leads = [] }) => {
  const navigate = useNavigate();

  const getLeadId = useCallback(
    (record) => record._id || record.id,
    []
  );

  const viewLead = useCallback(
    (record) => {
      const leadId = getLeadId(record);
      if (leadId) {
        navigate(`/leads/${leadId}`);
      }
    },
    [getLeadId, navigate]
  );

  const columns = [
    {
      title: "Reference Number",
      dataIndex: "referenceNumber",
      key: "referenceNumber",
      render: (ref, record) => (
        <Link
          onClick={(event) => {
            event.stopPropagation();
            viewLead(record);
          }}
        >
          <Text code>{ref || "N/A"}</Text>
        </Link>
      ),
    },
    {
      title: "From",
      dataIndex: "moving_from",
      key: "from",
      render: (from, record) => from || record.from || "N/A",
    },
    {
      title: "To",
      dataIndex: "moving_to",
      key: "to",
      render: (to, record) => to || record.to || "N/A",
    },
    {
      title: "Date",
      dataIndex: "move_date",
      key: "date",
      render: (date, record) => date || record.date || "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const statusMap = {
          new: { color: "blue", text: "New" },
          pending: { color: "gold", text: "Pending" },
          accepted: { color: "green", text: "Accepted" },
          rejected: { color: "red", text: "Rejected" },
          converted: { color: "default", text: "Converted" },
        };
        const cfg = statusMap[status] || { color: "default", text: status || "N/A" };
        return <Tag color={cfg.color}>{cfg.text}</Tag>;
      },
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => {
        if (!date) return "N/A";
        try {
          return format(new Date(date), "MMM dd, yyyy");
        } catch {
          return "Invalid date";
        }
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          aria-label="View lead"
          onClick={(event) => {
            event.stopPropagation();
            viewLead(record);
          }}
          style={{ opacity: 0.6 }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.6")}
        />
      ),
    },
  ];

  if (leads.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <Text type="secondary">No leads found for this customer</Text>
      </div>
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={leads}
      rowKey={(record) => record._id || record.id || record.referenceNumber}
      pagination={{ pageSize: 10 }}
      size="small"
      scroll={{ x: "max-content" }}
      onRow={(record) => ({
        onClick: () => viewLead(record),
        style: { cursor: "pointer" },
      })}
    />
  );
};

export default RelatedLeadsTable;

